/**
 * Segment's `context.page`, read by our own code.
 *
 * iOS 26 hides the query string and `document.referrer` from scripts it
 * classifies as trackers, which includes analytics.js from cdn.segment.com,
 * so the values it collects itself arrive bare. Ours are read first-party and
 * passed in as plain data. They mirror analytics.js's own defaults, so on
 * browsers that don't strip anything the payload is unchanged.
 *
 * `campaign` is deliberately left out: analytics.js derives it from
 * `context.page.search` when none is passed, which keeps its UTM parsing.
 */

export interface AttributionPage {
  url: string;
  path: string;
  search: string;
  referrer: string;
  title: string;
}

export interface AttributionContext {
  page: AttributionPage;
}

const canonicalPath = (canonical: string, fallback: string): string => {
  try {
    return new URL(canonical).pathname || "/";
  } catch {
    return fallback;
  }
};

// Same url/path derivation as analytics.js: a canonical link wins, and the
// hash is dropped.
const pageUrlAndPath = (
  href: string,
  pathname: string,
  search: string
): { url: string; path: string } => {
  const canonical = document
    .querySelector("link[rel='canonical']")
    ?.getAttribute("href");
  if (!canonical) {
    const hash = href.indexOf("#");
    return { url: hash === -1 ? href : href.slice(0, hash), path: pathname };
  }
  return {
    url: canonical.includes("?") ? canonical : canonical + search,
    path: canonicalPath(canonical, pathname),
  };
};

export const getAttributionContext = (): AttributionContext | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const { href, pathname, search } = window.location;
    return {
      page: {
        ...pageUrlAndPath(href, pathname, search),
        search,
        referrer: document.referrer,
        title: document.title,
      },
    };
  } catch {
    return undefined;
  }
};
